import { create } from 'zustand';

export interface LoadingMessage {
    id?: number | string;
    title?: string;
    message?: string;
    inBackground?: boolean;
}

interface LoadingMessageStore {
    loadingMessages: LoadingMessage[];
    addLoadingMessage: (message: LoadingMessage) => void;
    removeLoadingMessage: (message: LoadingMessage) => void;
    clearLoadingMessages: () => void;
}

export const useLoadingMessageStore = create<LoadingMessageStore>((set) => ({
    loadingMessages: [],

    addLoadingMessage: (message) =>
        set((state) => ({ loadingMessages: [...state.loadingMessages, message] })),

    removeLoadingMessage: (message) =>
        set((state) => ({
            loadingMessages: state.loadingMessages.filter(
                (m) =>
                    (message.id && m.id !== message.id) ||
                    (!message.id && !(m.title === message.title && m.message === message.message))
            ),
        })),

    clearLoadingMessages: () => set({ loadingMessages: [] }),
}));

export async function withLoadingMessage<T>(
    func: () => Promise<T> | T,
    message: LoadingMessage
): Promise<T> {
    const { addLoadingMessage, removeLoadingMessage } = useLoadingMessageStore.getState();
    try {
        addLoadingMessage(message);
        return await Promise.resolve(func());
    } finally {
        removeLoadingMessage(message);
    }
}

export class LoadingContext {
    private id: number;
    private defaultTitle: string;
    private inBackground: boolean;

    constructor() {
        this.id = Math.random();
        this.defaultTitle = 'Loading...';
        this.inBackground = false;
    }

    setDefaultTitle(title: string) {
        this.defaultTitle = title;
    }

    setToBackground(status: boolean) {
        this.inBackground = status;
    }

    setMessage(message: Partial<LoadingMessage>) {
        const { addLoadingMessage, removeLoadingMessage } = useLoadingMessageStore.getState();
        removeLoadingMessage({ id: this.id });
        addLoadingMessage({
            title: this.defaultTitle,
            inBackground: this.inBackground,
            ...message,
            id: this.id,
        });
    }

    clearAll() {
        useLoadingMessageStore.getState().clearLoadingMessages();
    }

    dispose() {
        useLoadingMessageStore.getState().removeLoadingMessage({ id: this.id });
    }
}
